import React, {
 useState, useMemo
} from 'react';
import {
 Card, CardContent
} from 'shared/components/ui/card';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from 'shared/components/ui/tooltip';
import {
 Skeleton
} from 'shared/components/ui/skeleton';
import {
 Pencil, Copy, Trash2, Plus, Search, GitBranch, ArrowRight
} from 'lucide-react';
import {
 WorkflowListItem, WorkflowStepType
} from '../types/workflow.types';

interface WorkflowCardsProps {
    workflows: WorkflowListItem[];
    isLoading: boolean;
    onEdit: (workflow: WorkflowListItem) => void;
    onDuplicate: (workflow: WorkflowListItem) => void;
    onDelete: (workflow: WorkflowListItem) => void;
    onNewWorkflow: () => void;
}

const stepTypeColors: Record<WorkflowStepType, string> = {
    FETCH: 'bg-blue-100 text-blue-700',
    API_CALL: 'bg-amber-100 text-amber-700',
    MUTATE: 'bg-green-100 text-green-700',
};

const WorkflowCards: React.FC<WorkflowCardsProps> = ({
    workflows, isLoading, onEdit, onDuplicate, onDelete, onNewWorkflow,
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredWorkflows = useMemo(() => {
        if (!searchTerm) return workflows;
        return workflows.filter((w) =>
            w.name.toLowerCase().includes(searchTerm.toLowerCase())
            || w.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [workflows, searchTerm]);

    if (isLoading) {
        return (
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-48" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({
 length: 3
}).map((_, i) => (
    <Card key={i}>
        <CardContent className="p-6 space-y-3">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <div className="flex gap-2 pt-2">
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-6 w-16" />
            </div>
        </CardContent>
    </Card>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <TooltipProvider>
            <div className="flex flex-col gap-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">Workflows</h1>
                        <p className="text-sm text-muted-foreground">Automate your data processing pipelines</p>
                    </div>
                    <Button onClick={onNewWorkflow}>
                        <Plus className="mr-2 h-4 w-4" />
                        Create New Workflow
                    </Button>
                </div>

                {/* Search */}
                {workflows.length > 0 && (
                    <div className="flex gap-3 items-center">
                        <div className="relative flex-grow max-w-sm">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search workflows..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                    </div>
                )}

                {/* Cards or Empty State */}
                {filteredWorkflows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-4 border rounded-md">
                        <div className="rounded-full bg-muted p-6">
                            <GitBranch className="h-10 w-10 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold">No workflows defined</h3>
                        <p className="text-sm text-muted-foreground text-center max-w-md">
                            Workflows automate your data processing pipelines. Create your first workflow to get started.
                        </p>
                        <Button onClick={onNewWorkflow}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Your First Workflow
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredWorkflows.map((workflow) => (
                            <Card
                                key={workflow.id}
                                className="cursor-pointer transition-all hover:shadow-md hover:ring-1 hover:ring-primary/20"
                                onClick={() => onEdit(workflow)}
                            >
                                <CardContent className="p-5 flex flex-col gap-3">
                                    {/* Name + Actions */}
                                    <div className="flex items-start justify-between">
                                        <h3 className="font-semibold text-base truncate flex-1 pr-2">
                                            {workflow.name}
                                        </h3>
                                        <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7"
                                                        onClick={() => onEdit(workflow)}
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent><p>Edit</p></TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7"
                                                        onClick={() => onDuplicate(workflow)}
                                                    >
                                                        <Copy className="h-3.5 w-3.5" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent><p>Duplicate</p></TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 text-red-600 hover:text-red-700"
                                                        onClick={() => onDelete(workflow)}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent><p>Delete</p></TooltipContent>
                                            </Tooltip>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    {workflow.description && (
                                        <p className="text-sm text-muted-foreground line-clamp-2">
                                            {workflow.description}
                                        </p>
                                    )}

                                    {/* Step type badges flow */}
                                    {workflow.steps.length > 0 && (
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            {workflow.steps.map((step, idx) => (
                                                <React.Fragment key={step.id}>
                                                    <Badge
                                                        variant="secondary"
                                                        className={`text-xs ${stepTypeColors[step.type]}`}
                                                    >
                                                        {step.type.replace('_', ' ')}
                                                    </Badge>
                                                    {idx < workflow.steps.length - 1 && (
                                                        <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    )}

                                    {/* Footer */}
                                    <div className="flex items-center justify-between pt-1 border-t mt-auto">
                                        <Badge variant="outline" className="text-xs">
                                            {workflow.steps.length} step{workflow.steps.length !== 1 ? 's' : ''}
                                        </Badge>
                                        <span className="text-xs text-muted-foreground">
                                            {workflow.updatedAt}
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </TooltipProvider>
    );
};

export default WorkflowCards;
